import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Stack, Text, Textarea } from '@mantine/core';
import { IconMapPinCheck } from '@tabler/icons-react';
import {
  useRidesAvailabilityService,
  RIDES_AVAILABILITY_STATUS,
  RIDES_ERROR_CODES,
  isRidesAvailabilityLive,
  isRidesBookingActive,
  type RidesAvailabilityView,
  type RidesBookingView,
} from '@actschurch/ccms-sdk';
import { ridesKeys, RIDE_DAY_POLL_MS } from '@/lib/queryKeys';
import { campusById, useRidesLookups } from '@/lib/lookups';
import { combineDateTime, countdown, formatShortDate, formatTime } from '@/lib/format';
import { hasErrorCode, isNotFoundError } from '@/lib/copy';
import { usePushCards } from '@/hooks/usePushCards';
import { photoRules, useCopy, visiblePhoto } from '@/lib/settings';
import { BackLink } from '@/components/rides/PageIntro';
import { PushCardStack } from '@/components/rides/PushCard';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { PlateChip } from '@/components/rides/PlateChip';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { StatusPill } from '@/components/ui/StatusPill';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { RideTicket } from '@/components/flows/RideTicket';
import { listingPill } from './Listings';
import { ConfirmSheet } from '@/components/flows/ConfirmSheet';
import { carWithColour, stampClock } from '@/components/flows/LiftCard';
import '@/styles/flows.css';

/** "1 seat" / "3 seats: 1 man and 2 women" (no "0 men"). */
function seatsText(b: RidesBookingView): string {
  if (b.partySize <= 1) return '1 seat';
  const parts: string[] = [];
  if (b.partyMaleCount) parts.push(b.partyMaleCount === 1 ? '1 man' : `${b.partyMaleCount} men`);
  if (b.partyFemaleCount) parts.push(b.partyFemaleCount === 1 ? '1 woman' : `${b.partyFemaleCount} women`);
  return `${b.partySize} seats${parts.length ? `: ${parts.join(' and ')}` : ''}`;
}

/**
 * Ride day for a driver: the riders first (with a child's own name on an under-18 booking), then a
 * big "I've arrived" that only works near departure (canMarkArrived / arrivalOpensAt, 409
 * arrival_too_early / arrival_too_late) and asks first, where riders meet you, the lift details and a
 * quiet "Cancel this lift" with its own confirmation.
 */
export default function DriveDayPage() {
  const { availabilityId } = useParams();
  const id = Number(availabilityId);
  const qc = useQueryClient();
  const availability = useRidesAvailabilityService();
  const lookups = useRidesLookups();
  const { items, push } = usePushCards({ availabilityId: id });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [arriveOpen, setArriveOpen] = useState(false);
  const [reason, setReason] = useState('');
  const { t, settings } = useCopy();
  const photos = photoRules(settings);

  const q = useQuery({
    queryKey: ridesKeys.availability.byId(id),
    queryFn: () => availability.getById(id),
    enabled: Number.isFinite(id) && id > 0,
    refetchInterval: RIDE_DAY_POLL_MS,
  });
  const l = q.data;

  // New bookings / cancellations noticed while polling.
  const prev = useRef<RidesAvailabilityView | null>(null);
  useEffect(() => {
    if (!l) return;
    const p = prev.current;
    if (p && l.seatsBooked > p.seatsBooked) push('A rider booked', `${l.seatsBooked} of ${l.seatsTotal} seats are now booked.`, 'ok');
    if (p && l.seatsBooked < p.seatsBooked) push('A rider cancelled', `${l.seatsOpen === 1 ? '1 seat is' : `${l.seatsOpen} seats are`} free again.`, 'info');
    prev.current = l;
  }, [l, push]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ridesKeys.availability.all });
    qc.invalidateQueries({ queryKey: ridesKeys.me.summary });
  };
  const arrived = useMutation({
    mutationFn: () => availability.markArrived(id),
    onSuccess: (a) => { setArriveOpen(false); refresh(); push('Your riders know you are here', `We told them you're at ${a.collectionName}.`, 'ok'); },
    onError: (e) => {
      if (hasErrorCode(e, RIDES_ERROR_CODES.ArrivalTooEarly) || hasErrorCode(e, RIDES_ERROR_CODES.ArrivalTooLate)) q.refetch();
    },
  });
  const cancel = useMutation({
    mutationFn: () => availability.cancel(id, { reason: reason.trim() || null }),
    onSuccess: () => { setCancelOpen(false); refresh(); },
  });

  if (q.isLoading) {
    return (
      <div className="rides-main rides-flow">
        <BackLink to="/drive/listings" label="My lifts" />
        <LoadingState label="Loading your lift" count={2} />
      </div>
    );
  }
  if (q.error || !l) {
    const gone = isNotFoundError(q.error);
    return (
      <div className="rides-main rides-flow">
        <BackLink to="/drive/listings" label="My lifts" />
        <ErrorState
          title={gone ? "This lift isn't available any more" : "We couldn't load your lift"}
          message={gone ? 'It may have been cancelled or removed.' : undefined}
          error={q.error}
          onRetry={gone ? undefined : () => q.refetch()}
          retrying={q.isFetching}
          secondaryAction={<Button component={Link} to="/drive/listings" variant="default" fullWidth>Back to My lifts</Button>}
        />
      </div>
    );
  }

  const live = isRidesAvailabilityLive(l.statusID);
  const isArrived = l.statusID === RIDES_AVAILABILITY_STATUS.Arrived || !!l.arrivedAt;
  const cancelled = l.statusID === RIDES_AVAILABILITY_STATUS.Cancelled;
  const completed = l.statusID === RIDES_AVAILABILITY_STATUS.Completed;
  // 254i: the server says when "I've arrived" is allowed; older servers don't send it.
  const serverKnows = typeof l.canMarkArrived === 'boolean';
  const canArrive = live && !isArrived && (serverKnows ? !!l.canMarkArrived : true);
  const tooEarly = live && !isArrived && serverKnows && !l.canMarkArrived && !!l.arrivalOpensAt;
  const tooLate = live && !isArrived && serverKnows && !l.canMarkArrived && !l.arrivalOpensAt;
  const when = combineDateTime(l.date, l.departureTime ?? l.serviceTimeStart);
  const soon = live ? countdown(when) : '';
  const bookings: RidesBookingView[] = l.bookings ?? [];
  const active = bookings.filter((b) => isRidesBookingActive(b.statusID));
  const sorted = [...active, ...bookings.filter((b) => !isRidesBookingActive(b.statusID))];
  const pickup = campusById(lookups.data, l.collectionLocationID);
  const arrivalError = hasErrorCode(arrived.error, RIDES_ERROR_CODES.ArrivalTooEarly)
    ? t('rideday.arrival_too_early', undefined, { time: stampClock(l.arrivalOpensAt, true) || 'closer to the time' })
    : hasErrorCode(arrived.error, RIDES_ERROR_CODES.ArrivalTooLate)
      ? t('rideday.arrival_too_late')
      : null;
  const lede = `${formatShortDate(l.date)}. ${l.departureTime ? `Leaves ${l.collectionName} at ${formatTime(l.departureTime)}` : `From ${l.collectionName}`}.${soon ? ` ${soon.charAt(0).toUpperCase()}${soon.slice(1)}.` : ''}`;

  return (
    <div className="rides-main rides-flow">
      <header className="rides-stephead rides-edithead">
        <BackLink to="/drive/listings" label="My lifts" />
        <h1 className="rides-title">{l.eventName}</h1>
        <p className="rides-lede">{lede}</p>
      </header>

      <div className="rides-flow-stack">
        {cancelled && (
          <Callout tone="bad" title="Lift cancelled">
            {t('rideday.driver_cancelled')}
            {l.cancelReason && <span style={{ display: 'block', marginTop: 6 }}>Your reason: “{l.cancelReason}”</span>}
          </Callout>
        )}
        {completed && <Callout tone="ok">{t('rideday.driver_completed')}</Callout>}
        {isArrived && !cancelled && (
          <div className="rides-status ok" role="status">
            <IconMapPinCheck size={28} aria-hidden="true" />
            <span className="txt">{t('rideday.arrived_done', undefined, { time: stampClock(l.arrivedAt) || 'just now' })}</span>
          </div>
        )}

        <SectionCard
          title={active.length === 1 ? 'Your rider' : 'Your riders'}
          description={active.length ? `${l.seatsBooked} of ${l.seatsTotal} seats booked` : undefined}
        >
          {sorted.length === 0 ? (
            <EmptyState plain title="Nobody has booked yet">{t('rideday.no_riders')}</EmptyState>
          ) : (
            <ul className="rides-riders">
              {sorted.map((b) => {
                const isActive = isRidesBookingActive(b.statusID);
                const childName = b.forMinor ? b.childName?.trim() || null : null;
                const child = b.forMinor ? childName ?? 'Child (name not given)' : null;
                // A child's booking is made from the guardian's account: that photo isn't the passenger, so it's only shown when allowed.
                const photo = visiblePhoto(b.riderPhotoUrl, photos.riderToDrivers && (!b.forMinor || photos.minors));
                return (
                  <li key={b.rideBookingID} data-cancelled={!isActive}>
                    {photos.enabled && <PersonAvatar src={photo} name={b.forMinor ? childName : b.riderName} size="md" decorative />}
                    <span className="txt">
                      <b>{child ?? b.riderName}</b>
                      {b.forMinor && <span>Under 18. Booked by {b.guardianName || b.riderName}{b.guardianRelationship ? ` (${b.guardianRelationship.toLowerCase()})` : ''}</span>}
                      <span>{isActive ? seatsText(b) : 'Cancelled their seat'}</span>
                      {isActive && (
                        <StatusPill tone={b.riderIdVerified ? 'ok' : 'warn'}>{b.riderIdVerified ? 'ID checked' : 'ID check not finished'}</StatusPill>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        {live && !isArrived && (
          <section aria-live="polite">
            <Button size="lg" fullWidth leftSection={<IconMapPinCheck size={22} aria-hidden="true" />} disabled={!canArrive} onClick={() => { arrived.reset(); setArriveOpen(true); }}>
              I've arrived
            </Button>
            <p className="rides-help" style={{ marginTop: 8, textAlign: 'center' }}>
              {tooEarly
                ? t('rideday.arrival_too_early', undefined, { time: stampClock(l.arrivalOpensAt, true) })
                : tooLate
                  ? t('rideday.arrival_too_late')
                  : t('rideday.arrived_help', undefined, { campus: l.collectionName })}
            </p>
          </section>
        )}

        {items.length > 0 && (
          <section aria-live="polite">
            <h2 className="rides-sectlabel" style={{ marginTop: 4 }}>Updates</h2>
            <PushCardStack items={items} />
          </section>
        )}

        <RideTicket
          tone={cancelled || completed ? 'muted' : isArrived ? 'ok' : 'live'}
          status={listingPill(l).label}
          eventName={l.eventName}
          date={l.date}
          departureTime={l.departureTime}
          serviceTimeStart={l.serviceTimeStart}
          collectionName={l.collectionName}
          dropOffName={l.dropOffName}
          meet={live ? { campus: pickup, campusName: l.collectionName } : null}
          rows={[
            { k: 'Seats booked', v: `${l.seatsBooked} of ${l.seatsTotal}` },
            { k: 'Your car', v: <>{carWithColour(l.vehicle)}<span style={{ display: 'block', marginTop: 6 }}><PlateChip plate={l.vehicle.plate} /></span></> },
            ...(l.notes?.trim() ? [{ k: 'Your note', v: `“${l.notes.trim()}”` }] : []),
          ]}
        />

        {live && (
          <div style={{ textAlign: 'center' }}>
            <button type="button" className="rides-linkbtn danger" onClick={() => { cancel.reset(); setCancelOpen(true); }}>Cancel this lift</button>
          </div>
        )}
        {!live && <Button variant="default" fullWidth component={Link} to="/drive/events">Offer another lift</Button>}
      </div>

      <ConfirmSheet opened={arriveOpen} onClose={() => setArriveOpen(false)} title="Tell your riders you've arrived?">
        <Stack gap="md">
          <Text>{t('rideday.arrived_confirm', undefined, { campus: l.collectionName })}</Text>
          {arrivalError ? <Callout tone="warn" live>{arrivalError}</Callout> : <ErrorAlert error={arrived.error} title="We couldn't tell your riders" />}
          {!arrivalError && <Button fullWidth size="lg" loading={arrived.isPending} onClick={() => arrived.mutate()}>Yes, tell them</Button>}
          <Button variant="default" fullWidth onClick={() => setArriveOpen(false)}>{arrivalError ? 'Close' : 'Not yet'}</Button>
        </Stack>
      </ConfirmSheet>

      <ConfirmSheet opened={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this lift?">
        <Stack gap="md">
          <Text>{active.length ? t('rideday.driver_cancel_riders') : t('rideday.driver_cancel_none')}</Text>
          <Textarea label="Why are you cancelling?" description={active.length ? 'Optional. Your riders will see this.' : 'Optional.'} placeholder="For example, car trouble" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} maxLength={300} />
          <ErrorAlert error={cancel.error} title="We couldn't cancel your lift" />
          <Button color="red" fullWidth loading={cancel.isPending} onClick={() => cancel.mutate()}>Yes, cancel this lift</Button>
          <Button variant="default" fullWidth onClick={() => setCancelOpen(false)}>Keep this lift</Button>
        </Stack>
      </ConfirmSheet>
    </div>
  );
}
